import React, { createContext, useState, useEffect } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { loadPermissions, getUserInfo as getUserInfoAPI } from "../API/User";
import { getLanguages, getLanguagesByCountryCode } from "../API/Country";
import i18next from "i18next";
require("dotenv").config();

const { REACT_APP_ALLOW_LANGUAGE_SELECTION } = process.env || {};
const languageSelctionAllowed =
  parseInt(REACT_APP_ALLOW_LANGUAGE_SELECTION, 10) === 1 || false;

const UserContext = createContext();

const UserContextProvider = ({ children }) => {
  const { authState, authService } = useOktaAuth();
  const [user, setUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState();

  useEffect(() => {
    const setUserLanguage = async () => {
      if (user) {
        const result = await getLanguages();
        const { data: languages = [] } = result;
        const found = languages
          .filter((l) => l.isAvailable)
          .find(
            (lang) =>
              lang.iso_code_2 === i18next.language ||
              `${lang.iso_code_2}-${user.locale}` === i18next.language,
          );
        if (found) {
          return;
        }
        const result1 = await getLanguagesByCountryCode(user.locale);
        const { data: countryLanguages = [] } = result1;
        if (countryLanguages) {
          for (let cLang of countryLanguages) {
            if (
              cLang.language_code === i18next.language ||
              `${cLang.language_code}-${user.locale}` === i18next.language
            ) {
              return;
            } else {
              i18next.changeLanguage(`${cLang.language_code}-${user.locale}`);
              return;
            }
          }
        }
      }
    };
    if (languageSelctionAllowed) {
      setUserLanguage();
    } else {
      i18next.changeLanguage("en");
    }
  }, [user]);

  const getUserPermissions = async () => {
    if (!authState.isAuthenticated) {
      setUserPermissions(null);
      return null;
    }
    if (userPermissions) {
      return userPermissions;
    }
    const result = await loadPermissions();
    const { data } = result || {};
    setUserPermissions(data);
    return data;
  };

  const hasPermission = async (recordType, action) => {
    const ups = await getUserPermissions();
    if (!ups) {
      return false;
    }

    let rtPermission = ups[recordType];

    if (rtPermission) {
      let actionPermission = rtPermission[action];
      if (actionPermission !== undefined) {
        return actionPermission;
      }
      actionPermission = rtPermission["*"];
      if (actionPermission !== undefined) {
        return actionPermission;
      }
    }
    rtPermission = ups["*"];
    if (rtPermission) {
      let actionPermission = rtPermission[action];
      if (actionPermission !== undefined) {
        return actionPermission;
      }
      actionPermission = rtPermission["*"];
      if (actionPermission !== undefined) {
        return actionPermission;
      }
    }

    return false;
  };

  const hasRecordPermission = (permissions, action) => {
    if (!permissions) {
      return false;
    }
    let actionPermission = permissions[action];
    if (actionPermission !== undefined) {
      return actionPermission;
    }
    actionPermission = permissions["*"];
    if (actionPermission !== undefined) {
      return actionPermission;
    }

    return false;
  };

  const hasAnyRecordPermission = async (recordTypes) => {
    const ups = await getUserPermissions();
    if (!ups) {
      return false;
    }
    for (let rt of recordTypes) {
      const rp = ups[rt];
      if (!rp) {
        continue;
      }
      for (let action in rp) {
        if (rp[action] === true) {
          return true;
        }
      }
    }
    let rp = ups["*"];
    if (rp) {
      for (let action in rp) {
        if (rp[action] === true) {
          return true;
        }
      }
    }
    return false;
  };

  const getUserInfo = async () => {
    if (!authState.isAuthenticated) {
      setUser(null);
      return null;
    }
    if (!user) {
      try {
        const result = await getUserInfoAPI();
        const { data } = result || {};
        setUser(data);
        return data;
      } catch (e) {
        console.log(e);
      }
    }
    return user;
  };

  const logout = async () => {
    authService.logout("/logout");
  };

  return (
    <UserContext.Provider
      value={[
        getUserInfo,
        getUserPermissions,
        hasPermission,
        hasRecordPermission,
        hasAnyRecordPermission,
        logout,
      ]}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserContextProvider };
